import { useMutation, useQuery, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { getRouteApi, Link } from '@tanstack/react-router';
import { isCriticalLabInterpretation } from '@wodi/domain';
import type { LabReportView, LabSampleWorkView } from '@wodi/shared';
import {
  Button,
  Dialog,
  DialogActions,
  LoadingBlock,
  Notice,
  PageHeader,
  Select,
  Tag,
  TextArea,
  useShortcut,
} from '@wodi/ui';
import { useState, type ReactNode } from 'react';

import {
  correctLabReport,
  labKeys,
  labSampleWorkQuery,
  labValueSetQuery,
  markLabReportInError,
  receiveLabSample,
  rejectLabSample,
  returnLabReport,
  validateLabReport,
} from '../api/lab.ts';
import { can, sessionQuery } from '../api/session.ts';
import { useOnline } from '../hooks/use-online.ts';
import { usePrintLabels } from '../lab/collect-dialog.tsx';
import { REPORT_STATUS_LABELS, REPORT_STATUS_TAGS, SAMPLE_STATUS_LABELS } from '../lab/format.ts';
import styles from '../lab/lab.module.css';
import { screen } from '../layout/screen.tsx';
import { PrintReportDialog } from '../lab/print-report-dialog.tsx';
import { LabelRun } from '../lab/prints.tsx';
import { CultureEntryForm, CultureSummary, PreliminaryReports } from '../lab/culture-panel.tsx';
import { QcHoldNotice } from '../lab/qc-hold-notice.tsx';
import { ResultEntryForm } from '../lab/result-entry.tsx';
import { ResultTable } from '../lab/result-table.tsx';
import { PatientBannerLoader } from '../patients/patient-banner.tsx';
import { formatInstant } from '../patients/format.ts';
import { ReasonDialog } from '../patients/reason-dialog.tsx';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';

const route = getRouteApi('/_app/lab/samples/$sampleId');

/**
 * One sample and every request on it (plan §8.4–8.6): result entry for the technologist,
 * validation as a separate, deliberate step for another person, then the released report
 * with print, correction and entered in error. The patient banner comes first.
 */
export function LabSamplePage() {
  const { sampleId } = route.useParams();
  const { data: session } = useSuspenseQuery(sessionQuery);
  const work = useQuery(labSampleWorkQuery(sampleId));

  if (session === null || !can(session, 'lab.read')) {
    return (
      <>
        <PageHeader title="Lab sample" />
        <Notice tone="info">
          You don't have permission to see lab samples. Ask an administrator if you need it.
        </Notice>
      </>
    );
  }
  if (work.isPending) return <LoadingBlock label="Loading the sample" rows={6} />;
  if (work.isError)
    return (
      <>
        <PageHeader title="Lab sample" />
        <Notice
          tone="error"
          action={
            <Button dense onPress={() => void work.refetch()}>
              Try again
            </Button>
          }
        >
          {errorText(work.error, "The sample couldn't be loaded. Try again.")}
        </Notice>
      </>
    );
  const { sample } = work.data;
  return (
    <div className={styles.page}>
      <PatientBannerLoader patientId={sample.patient.id} />
      <p className={styles.meta}>
        <Link to="/lab">Back to the lab worklist</Link>
      </p>
      <PageHeader
        title={`Sample ${sample.sampleNumber}`}
        actions={<SampleActions work={work.data} />}
      />
      <SampleFacts work={work.data} />
      {sample.status === 'rejected' ? (
        <Notice tone="info">
          {`This sample was rejected: ${sample.rejectionReason ?? 'no reason recorded'}. Collect a new sample from the worklist.`}
        </Notice>
      ) : null}
      {work.data.reports.length === 0 ? (
        <Notice tone="info">No results are being worked on for this sample.</Notice>
      ) : null}
      {work.data.reports.map((report) => (
        <ReportPanel key={report.id} report={report} work={work.data} />
      ))}
    </div>
  );
}

function SampleFacts({ work }: { work: LabSampleWorkView }) {
  const { sample } = work;
  const facts: { label: string; value: ReactNode }[] = [
    { label: 'Sample no.', value: <span className="wodi-id">{sample.sampleNumber}</span> },
    { label: 'Specimen', value: `${sample.specimenType.display}, ${sample.container.display}` },
    { label: 'Tests', value: sample.orders.map((o) => o.testName).join(', ') },
    {
      label: 'Collected',
      value: `${formatInstant(sample.collectedAt)} by ${sample.collectedBy.displayName}`,
    },
    {
      label: 'Status',
      value:
        sample.receivedAt === null
          ? SAMPLE_STATUS_LABELS[sample.status]
          : `${SAMPLE_STATUS_LABELS[sample.status]}, received ${formatInstant(sample.receivedAt)}`,
    },
    { label: 'Requested by', value: work.orderingClinician ?? 'Not recorded' },
  ];
  return (
    <dl className={styles.factGrid}>
      {facts.map((fact) => (
        <div key={fact.label}>
          <dt className={styles.meta}>{fact.label}</dt>
          <dd>{fact.value}</dd>
        </div>
      ))}
    </dl>
  );
}

function SampleActions({ work }: { work: LabSampleWorkView }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const queryClient = useQueryClient();
  const online = useOnline();
  const { sample, settings } = work;
  const { runRef, print, record } = usePrintLabels();
  const [rejecting, setRejecting] = useState(false);
  const receive = useMutation({
    mutationFn: () => receiveLabSample(sample.id),
    onSuccess: async () => {
      toasts.show({ title: 'Sample received' });
      await queryClient.invalidateQueries({ queryKey: labKeys.work });
    },
  });
  if (session === null || !can(session, 'lab.collect')) return null;
  const live = sample.status === 'collected' || sample.status === 'received';
  return (
    <div className={styles.actions}>
      {live ? (
        <Button
          isDisabled={!online}
          onPress={() => {
            print([sample], settings.labelCopies);
          }}
        >
          Reprint label
        </Button>
      ) : null}
      {settings.useReceiveStep && sample.status === 'collected' ? (
        <Button
          isDisabled={!online}
          isPending={receive.isPending}
          pendingLabel="Receiving…"
          onPress={() => {
            receive.mutate();
          }}
        >
          Receive in lab
        </Button>
      ) : null}
      {live ? (
        <Button
          isDisabled={!online}
          onPress={() => {
            setRejecting(true);
          }}
        >
          Reject sample
        </Button>
      ) : null}
      {receive.isError ? (
        <Notice tone="error">{errorText(receive.error, "The sample wasn't received.")}</Notice>
      ) : null}
      {record.isError ? (
        <Notice tone="error">{errorText(record.error, "The reprint wasn't recorded.")}</Notice>
      ) : null}
      {/* The label to reprint: kept off screen, printed on demand. */}
      <div className={styles.offscreen}>
        <LabelRun samples={[sample]} copies={settings.labelCopies} runRef={runRef} />
      </div>
      {rejecting ? (
        <RejectDialog
          sampleId={sample.id}
          onClose={() => {
            setRejecting(false);
          }}
        />
      ) : null}
    </div>
  );
}

function RejectDialog({ sampleId, onClose }: { sampleId: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const reasons = useQuery(labValueSetQuery('lab_rejection_reason'));
  const [reason, setReason] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [tried, setTried] = useState(false);
  const reject = useMutation({
    mutationFn: () => rejectLabSample(sampleId, reason ?? '', note.trim() === '' ? null : note),
    onSuccess: async () => {
      toasts.show({ title: 'Sample rejected' });
      await queryClient.invalidateQueries({ queryKey: labKeys.work });
      onClose();
    },
  });
  const active = (reasons.data ?? []).filter((r) => r.status === 'active');
  return (
    <Dialog
      title="Reject sample"
      isOpen
      onOpenChange={(open) => {
        if (!open) onClose();
      }}
    >
      <div className={styles.form}>
        <p>The request goes back to To collect so a new sample can be taken.</p>
        {reasons.isError ? (
          <Notice tone="error">
            {errorText(reasons.error, "The rejection reasons couldn't be loaded.")}
          </Notice>
        ) : null}
        {reasons.isSuccess && active.length === 0 ? (
          <Notice tone="info">
            No rejection reasons are set up. Ask the lab manager to add them in Settings, Lab.
          </Notice>
        ) : null}
        <Select
          label="Reason"
          options={active.map((r) => ({ id: r.code, label: r.display }))}
          value={reason}
          onChange={(value) => {
            setReason(value === null ? null : String(value));
          }}
          {...(tried && reason === null ? { errorMessage: 'Choose a reason.' } : {})}
        />
        <TextArea label="Note (optional)" value={note} onChange={setNote} rows={2} />
        {reject.isError ? (
          <Notice tone="error">{errorText(reject.error, "The sample wasn't rejected.")}</Notice>
        ) : null}
      </div>
      <DialogActions>
        <Button variant="quiet" onPress={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          isPending={reject.isPending}
          pendingLabel="Rejecting…"
          onPress={() => {
            setTried(true);
            if (reason !== null) reject.mutate();
          }}
        >
          Reject sample
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function ReportPanel({ report, work }: { report: LabReportView; work: LabSampleWorkView }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  if (session === null) return null;
  const rejected = work.sample.status === 'rejected';
  const editing = report.status === 'in_progress' && can(session, 'lab.result.enter') && !rejected;
  return (
    <section className={`${styles.section} ${screen.panel}`} aria-labelledby={`r-${report.id}`}>
      <div className={styles.actions}>
        <h2 id={`r-${report.id}`} className={styles.sectionHeading}>
          {report.testName}
        </h2>
        <Tag state={REPORT_STATUS_TAGS[report.status]}>{REPORT_STATUS_LABELS[report.status]}</Tag>
        {report.version > 1 ? <span className={styles.meta}>Version {report.version}</span> : null}
      </div>
      {report.correctionReason === null ? null : (
        <p>
          <strong>Correction: </strong>
          {report.correctionReason}
        </p>
      )}
      {report.status === 'in_progress' && report.statusReason !== null ? (
        <Notice tone="info">{`Returned for correction: ${report.statusReason}`}</Notice>
      ) : null}
      {report.workflow === 'culture' ? (
        editing ? (
          <CultureEntryForm key={`${report.id}-${String(report.revision)}`} report={report} />
        ) : report.culture === null ? (
          <p className={styles.meta}>No culture recorded yet.</p>
        ) : (
          <CultureSummary culture={report.culture} label={report.testName} />
        )
      ) : null}
      {report.workflow === 'culture' ? <PreliminaryReports report={report} work={work} /> : null}
      {editing ? (
        <ResultEntryForm key={report.id} report={report} formats={work.analytes} />
      ) : report.results.length === 0 ? (
        report.workflow === 'culture' ? null : (
          <p className={styles.meta}>No results entered yet.</p>
        )
      ) : (
        <ResultTable report={report} showPrevious />
      )}
      {editing || report.comment === null ? null : <p>{report.comment}</p>}
      {report.status === 'awaiting_validation' || report.status === 'technically_validated' ? (
        <ValidationActions report={report} work={work} />
      ) : null}
      {report.status === 'final' ? <ReleasedActions report={report} work={work} /> : null}
      <History report={report} />
    </section>
  );
}

/** Validation is its own step, by someone other than the person who entered (C8). */
function ValidationActions({ report, work }: { report: LabReportView; work: LabSampleWorkView }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const queryClient = useQueryClient();
  const online = useOnline();
  const [returning, setReturning] = useState(false);
  const twoStage = work.settings.validationStages === 'two';
  const action = report.status === 'technically_validated' ? 'authorise' : 'validate';
  const permission = action === 'authorise' ? 'lab.result.authorise' : 'lab.result.validate';
  const me = session?.user.id;
  const enteredByMe =
    report.submitted?.by.id === me || report.results.some((r) => r.enteredBy.id === me);
  const validatedByMe = report.technicallyValidated?.by.id === me;
  const mustDiffer =
    work.settings.validatorMustDiffer && (enteredByMe || (action === 'authorise' && validatedByMe));
  const held = report.qcHolds.length > 0;
  const allowed = session !== null && can(session, permission) && !mustDiffer && !held;
  const critical =
    report.results.some((r) => isCriticalLabInterpretation(r.interpretation)) ||
    report.culture?.critical === true;
  const label =
    action === 'authorise'
      ? 'Authorise and release'
      : twoStage
        ? 'Validate'
        : 'Validate and release';

  const validate = useMutation({
    mutationFn: () => validateLabReport(report.id, action, report.revision),
    onSuccess: async (next) => {
      toasts.show({ title: next.status === 'final' ? 'Report released' : 'Results validated' });
      await queryClient.invalidateQueries({ queryKey: labKeys.work });
    },
  });
  const giveBack = useMutation({
    mutationFn: (reason: string) => returnLabReport(report.id, reason),
    onSuccess: async () => {
      toasts.show({ title: 'Returned for correction' });
      setReturning(false);
      await queryClient.invalidateQueries({ queryKey: labKeys.work });
    },
  });
  useShortcut(
    'mod+enter',
    () => {
      if (allowed && online) validate.mutate();
    },
    { allowInFields: true, enabled: allowed },
  );
  if (session === null || !can(session, permission)) {
    return (
      <p className={styles.meta}>
        {action === 'authorise'
          ? 'Waiting for clinical validation by a pathologist or lab manager.'
          : 'Waiting for validation by a senior technologist.'}
      </p>
    );
  }
  return (
    <div className={styles.stack}>
      <QcHoldNotice holds={report.qcHolds} />
      {mustDiffer ? (
        <Notice tone="info">
          {action === 'authorise' && validatedByMe && !enteredByMe
            ? 'You validated these results. Another person must authorise them.'
            : 'You entered these results. Another person must validate them.'}
        </Notice>
      ) : null}
      {critical && allowed ? (
        <p className={styles.critical}>
          This report has a critical result. Releasing it notifies the ordering clinician at once.
        </p>
      ) : null}
      {validate.isError ? (
        <Notice tone="error">{errorText(validate.error, "The results weren't validated.")}</Notice>
      ) : null}
      <div className={styles.actions}>
        <Button
          variant="primary"
          isDisabled={!allowed || !online}
          isPending={validate.isPending}
          pendingLabel="Validating…"
          onPress={() => {
            validate.mutate();
          }}
          aria-keyshortcuts="Control+Enter"
        >
          {label}
        </Button>
        <Button
          isDisabled={!online}
          onPress={() => {
            setReturning(true);
          }}
        >
          Return for correction
        </Button>
      </div>
      <ReasonDialog
        title="Return for correction"
        isOpen={returning}
        onOpenChange={setReturning}
        confirmLabel="Return for correction"
        pendingLabel="Returning…"
        isPending={giveBack.isPending}
        error={giveBack.isError ? errorText(giveBack.error, "The results weren't returned.") : null}
        onConfirm={(reason) => {
          giveBack.mutate(reason);
        }}
      >
        <p>The technologist sees the reason and corrects the results before submitting again.</p>
      </ReasonDialog>
    </div>
  );
}

function ReleasedActions({ report, work }: { report: LabReportView; work: LabSampleWorkView }) {
  const { data: session } = useSuspenseQuery(sessionQuery);
  const queryClient = useQueryClient();
  const online = useOnline();
  const [correcting, setCorrecting] = useState(false);
  const [inError, setInError] = useState(false);
  const [printing, setPrinting] = useState(false);
  const correct = useMutation({
    mutationFn: (reason: string) => correctLabReport(report.id, reason),
    onSuccess: async () => {
      toasts.show({ title: 'Correction started', description: 'Enter the corrected results.' });
      setCorrecting(false);
      await queryClient.invalidateQueries({ queryKey: labKeys.work });
    },
  });
  const markInError = useMutation({
    mutationFn: (reason: string) => markLabReportInError(report.id, reason),
    onSuccess: async () => {
      toasts.show({ title: 'Report marked entered in error' });
      setInError(false);
      await queryClient.invalidateQueries({ queryKey: labKeys.work });
    },
  });
  if (session === null) return null;
  return (
    <div className={styles.actions}>
      {can(session, 'lab.report.print') ? (
        <Button
          onPress={() => {
            setPrinting(true);
          }}
        >
          Print report
        </Button>
      ) : null}
      {can(session, 'lab.result.correct') ? (
        <>
          <Button
            isDisabled={!online}
            onPress={() => {
              setCorrecting(true);
            }}
          >
            Correct report
          </Button>
          <Button
            isDisabled={!online}
            onPress={() => {
              setInError(true);
            }}
          >
            Mark entered in error
          </Button>
        </>
      ) : null}
      {printing ? (
        <PrintReportDialog
          data={{
            facilityName: session.facility.name,
            paper: work.settings.reportPaper,
            patient: {
              displayName: work.sample.patient.displayName,
              patientNumber: work.sample.patient.patientNumber,
              sex: work.sample.patient.sex.display,
              age: work.sample.patient.age,
            },
            sampleNumber: work.sample.sampleNumber,
            specimen: work.sample.specimenType.display,
            collectedAt: work.sample.collectedAt,
            receivedAt: work.sample.receivedAt,
            requester: work.orderingClinician,
            report,
          }}
          onClose={() => {
            setPrinting(false);
          }}
        />
      ) : null}
      <ReasonDialog
        title="Correct report"
        isOpen={correcting}
        onOpenChange={setCorrecting}
        confirmLabel="Start correction"
        pendingLabel="Starting…"
        isPending={correct.isPending}
        error={correct.isError ? errorText(correct.error, "The correction didn't start.") : null}
        onConfirm={(reason) => {
          correct.mutate(reason);
        }}
      >
        <p>
          A new version is made from this one and goes through validation again. This version stays
          visible until the correction is released, and the clinician is told.
        </p>
      </ReasonDialog>
      <ReasonDialog
        title="Mark entered in error"
        isOpen={inError}
        onOpenChange={setInError}
        confirmLabel="Mark entered in error"
        pendingLabel="Marking…"
        isPending={markInError.isPending}
        error={
          markInError.isError ? errorText(markInError.error, "The report wasn't marked.") : null
        }
        onConfirm={(reason) => {
          markInError.mutate(reason);
        }}
      >
        <p>
          For results entered against the wrong patient or sample. The report is kept, never
          deleted, and the clinician must acknowledge it.
        </p>
      </ReasonDialog>
    </div>
  );
}

function History({ report }: { report: LabReportView }) {
  if (report.versions.length < 2) return null;
  return (
    <details>
      <summary>History, {report.versions.length} versions</summary>
      <ul className={styles.list}>
        {report.versions.map((v) => (
          <li key={v.id} className={styles.listRow}>
            <span>Version {v.version}</span>
            <span>
              {REPORT_STATUS_LABELS[v.status]}
              {v.releasedAt === null ? '' : `, released ${formatInstant(v.releasedAt)}`}
              {v.correctionReason === null ? '' : `. Reason: ${v.correctionReason}`}
            </span>
            <span />
          </li>
        ))}
      </ul>
    </details>
  );
}
